import * as React from 'react'

import { Box, Button, Section, Text } from '@factorialco/deprecated-design-system'
import moment from 'moment'
import type { $Values } from 'utility-types/dist/utility-types'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendToUI, dateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'

// TODO: private form object from another component
import CompanySettingsVersionForm from 'modules/attendance/components/SettingItemPage/TimeTracking/CompanySettingsVersions/form'
import { useCreateAttendanceCompanySettingsVersion } from 'modules/attendance/hooks/company_settings_versions/useCreateAttendanceCompanySettingsVersion'
import { useCurrentCompanySettingsVersion } from 'modules/attendance/hooks/company_settings_versions/useCurrentCompanySettingsVersion'
import { useFutureCompanySettingsVersion } from 'modules/attendance/hooks/company_settings_versions/useFutureCompanySettingsVersion'
import { useUpdateAttendanceCompanySettingsVersion } from 'modules/attendance/hooks/company_settings_versions/useUpdateAttendanceCompanySettingsVersion'
import { OVERNIGHT_SHIFTS_CALCULATION_METHODS } from 'modules/attendance/lib/enums'
import companySettingsVersionsCollection from 'modules/attendance/store/collections/companySettingsVersions'
import Loading from 'modules/layout/components/Page/Loading'

const options = () => {
  const labelOptions = {
    legacy: '',
    beginning_shift: i18n.t('time_tracking.settings.overnight_shifts.options.beginning_shift'),
    calendar_day: i18n.t('time_tracking.settings.overnight_shifts.options.calendar_day'),
  }

  return Object.values(OVERNIGHT_SHIFTS_CALCULATION_METHODS).map((option) => ({
    label: labelOptions[option],
    value: option,
  }))
}

const OVERNIGHT_OPTIONS = {
  beginningShift: 'beginning_shift',
  calendarDay: 'calendar_day',
  legacy: 'legacy',
  empty: '',
}

type OvernightOptions = $Values<typeof OVERNIGHT_OPTIONS>

export interface Action {
  type: 'UPDATE_OVERNIGHT_DATA' | 'UPDATE_DATA'
  payload: Record<string, unknown>
}
export interface State {
  prevOvernightMethod: OvernightOptions
  overnightMethod: OvernightOptions
  overnightMethodLast: OvernightOptions
  overnightMethodCurrent: OvernightOptions
  showButton: boolean
  showDescription: boolean
  labelEffectiveOn: string
  effectiveOn: string
  showLabelEffectiveOn: boolean
  showModal: boolean
}

const overnightMethodIsValid = (method: OvernightOptions) => {
  return Object.keys(OVERNIGHT_SHIFTS_CALCULATION_METHODS).includes(method)
}

const reducer = (state: State, action: Action) => {
  const { type, payload } = action

  switch (type) {
    case 'UPDATE_OVERNIGHT_DATA':
      const overnightMethod = String(payload.overnightMethod)

      return {
        ...state,
        ...payload,
        prevOvernightMethod: state.overnightMethod,
        showButton: overnightMethodIsValid(overnightMethod),
        showDescription: overnightMethodIsValid(overnightMethod),
      }
    case 'UPDATE_DATA':
      return { ...state, ...payload }
  }
}

const OvernightShifts = () => {
  const [form, setForm] = React.useState<CompanySettingsVersionForm>(
    new CompanySettingsVersionForm({})
  )
  const initialState = {
    prevOvernightMethod: OVERNIGHT_OPTIONS.empty,
    overnightMethod: OVERNIGHT_OPTIONS.legacy,
    overnightMethodLast: OVERNIGHT_OPTIONS.legacy,
    overnightMethodCurrent: OVERNIGHT_OPTIONS.legacy,
    showButton: false,
    showDescription: true,
    labelEffectiveOn: '',
    effectiveOn: dateToString(moment(new Date()), 'ui'),
    showLabelEffectiveOn: false,
    showModal: false,
  }

  const [state, dispatch] = React.useReducer(reducer, initialState)

  const { currentSettingVersion } = useCurrentCompanySettingsVersion()
  const { futureSettingVersion } = useFutureCompanySettingsVersion()
  const { createSettingsVersion } = useCreateAttendanceCompanySettingsVersion()
  const { updateSettingsVersion } = useUpdateAttendanceCompanySettingsVersion()

  const {
    prevOvernightMethod,
    overnightMethod,
    showButton,
    showDescription,
    overnightMethodCurrent,
    overnightMethodLast,
    labelEffectiveOn,
    effectiveOn,
    showLabelEffectiveOn,
    showModal,
  } = state

  const { loading } = useAsyncData(async () => {
    const [currentRes, futureRes] = await Promise.all([
      currentSettingVersion(),
      futureSettingVersion(),
    ])

    const currentHandled = handleResult(currentRes, () => undefined)
    if (!currentHandled.success) return

    const { companySettingsVersion: currentCompanySettingsVersion } = currentHandled.data
    if (!currentCompanySettingsVersion) return

    const futureHandled = handleResult(futureRes, () => undefined)
    if (!futureHandled.success) return

    const { companySettingsVersion: futureCompanySettingsVersion } = futureHandled.data
    if (!futureCompanySettingsVersion) return

    const { effectiveOn, overnightShiftsCalculationMethod } = futureCompanySettingsVersion
    createCompanySettingsVersionForm(futureCompanySettingsVersion)

    dispatch({
      type: 'UPDATE_OVERNIGHT_DATA',
      payload: {
        overnightMethod: overnightShiftsCalculationMethod,
        overnightMethodLast: overnightShiftsCalculationMethod,
        overnightMethodCurrent: currentCompanySettingsVersion.overnightShiftsCalculationMethod,
        labelEffectiveOn: effectiveOn,
      },
    })
  }, [])

  const createCompanySettingsVersionForm = (future: Record<string, string | number | null>) => {
    const lastDate = stringToDate(future.effectiveOn as string, 'backend')
    const id = lastDate.diff(new Date()) < 0 ? null : future.id

    setForm(
      new CompanySettingsVersionForm({
        id: id,
        overnight_shifts_calculation_method: String(future.overnightShiftsCalculationMethod),
        effective_on: dateBackendToUI(String(future.effectiveOn)),
      })
    )
  }

  useEffect(() => {
    const labelEffectiveOnDate = stringToDate(labelEffectiveOn, 'backend')
    dispatch({
      type: 'UPDATE_DATA',
      payload: {
        showLabelEffectiveOn:
          labelEffectiveOnDate.diff(new Date()) > 0 &&
          overnightMethodCurrent !== overnightMethod &&
          overnightMethodLast == overnightMethod,
      },
    })
  }, [labelEffectiveOn, overnightMethod, overnightMethodCurrent, overnightMethodLast])

  const saveCompanySettings = async () => {
    const id = String(form.get('id').value)
    const calculationMethodInForm = form.get('overnight_shifts_calculation_method').value
    const effectiveOnInForm =
      form.parsedData().effective_on ?? dateToString(moment(new Date()), 'backend')

    if (id) {
      await updateSettingsVersion({
        id,
        effectiveOn: effectiveOnInForm,
        overnightShiftsCalculationMethod: calculationMethodInForm,
      })
    } else {
      const result = await createSettingsVersion({
        effectiveOn: effectiveOnInForm,
        overnightShiftsCalculationMethod: calculationMethodInForm,
      })

      const handled = handleResult(result, () => undefined)
      if (!handled.success) return

      const newId = handled.data.companySettingsVersion?.id
      form.setValues({ id: newId })
    }
    await companySettingsVersionsCollection.fetch()

    dispatch({
      type: 'UPDATE_DATA',
      payload: {
        overnightMethodLast: overnightMethod,
        showButton: false,
        labelEffectiveOn: effectiveOnInForm,
        showModal: false,
      },
    })
  }

  const onChangeSelector = (method: string) => {
    const previousSetting = form.get('overnight_shifts_calculation_method').value
    form.setValues({
      overnight_shifts_calculation_method: method,
    })
    track(trackingEvents.attendance.overnightShiftConfigStart, {
      new_setting: method,
      previous_setting: previousSetting,
    })
    dispatch({ type: 'UPDATE_OVERNIGHT_DATA', payload: { overnightMethod: method } })
  }

  const onClickSelector = () => {
    dispatch({ type: 'UPDATE_DATA', payload: { showModal: true } })
    track(trackingEvents.attendance.overnightShiftConfigAdd, {
      new_setting: overnightMethod,
      previous_setting: prevOvernightMethod,
      effective_date: effectiveOn,
    })
  }

  const onChangeModal = () => {
    dispatch({ type: 'UPDATE_DATA', payload: { effectiveOn: form.get('effective_on').value } })
  }

  const onCloseModal = () => {
    dispatch({ type: 'UPDATE_DATA', payload: { showModal: false } })
  }

  const onSubmitModal = () => {
    saveCompanySettings()
    track(trackingEvents.attendance.overnightShiftConfigSubmit, {
      new_setting: overnightMethod,
      previous_setting: prevOvernightMethod,
      effective_date: effectiveOn,
    })
  }

  const methodsDescription: { [key: string]: string } = {
    beginning_shift: i18n.t(
      'time_tracking.settings.overnight_shifts.options_descriptions.beginning_shift'
    ),
    calendar_day: i18n.t(
      'time_tracking.settings.overnight_shifts.options_descriptions.calendar_day'
    ),
  }

  const lastOption: { [key: string]: string } = {
    beginning_shift: i18n.t(
      'time_tracking.settings.overnight_shifts.current_option_text.beginning_shift'
    ),
    calendar_day: i18n.t(
      'time_tracking.settings.overnight_shifts.current_option_text.calendar_day'
    ),
  }

  const showHelpCenterLink = {
    href: helpCenterUrl('time_tracking_overnight_shifts_setup'),
    label: i18n.t('time_tracking.settings.overnight_shifts.description_link'),
  }

  return (
    <Section
      illustration="night"
      title={i18n.t('time_tracking.settings.overnight_shifts.title')}
      description={i18n.t('time_tracking.settings.overnight_shifts.description')}
      link={showHelpCenterLink}
    >
      <Box gap="s24">
        {loading && <Loading />}
        {!loading && (
          <Box
            flexDirection="column"
            gap="s16"
            background="grey200"
            padding="s24"
            border={{
              all: {
                color: 'grey400',
                width: 's1',
                style: 'solid',
              },
            }}
            borderRadius={{
              all: 'abs008',
            }}
          >
            <SelectField
              label={i18n.t('time_tracking.settings.overnight_shifts.select_title')}
              value={overnightMethod}
              none={i18n.t('common.choose')}
              options={options()}
              onChange={onChangeSelector}
              mandatory
            />
            {showModal && (
              <Modal
                illustration="alert"
                title={i18n.t('time_tracking.settings.overnight_shifts.modal.title')}
                description={
                  <Box gap="s16" paddingY="s16">
                    <Text.Normal>
                      {i18n.t('time_tracking.settings.overnight_shifts.modal.description.first')}
                    </Text.Normal>
                    <Text.Normal>
                      {i18n.t('time_tracking.settings.overnight_shifts.modal.description.second')}
                    </Text.Normal>
                  </Box>
                }
                onClose={onCloseModal}
                onSubmit={onSubmitModal}
                footer={
                  <>
                    <Button type="secondary" onClick={onCloseModal}>
                      {i18n.t('common.cancel')}
                    </Button>
                    <Button disabled={false} onClick={onSubmitModal}>
                      {i18n.t('common.apply')}
                    </Button>
                  </>
                }
              >
                <Box>
                  <DayPicker
                    field={form.get('effective_on')}
                    value={effectiveOn}
                    label={i18n.t('time_tracking.settings.overnight_shifts.modal.label')}
                    minDate={moment().subtract(1, 'days')}
                    onChange={onChangeModal}
                  />
                </Box>
              </Modal>
            )}
            {showLabelEffectiveOn && (
              <Text.Normal size="200" color="grey800" weight="medium">
                {i18n.t('time_tracking.settings.overnight_shifts.current_option_date', {
                  effective_date: dateBackendToUI(labelEffectiveOn),
                })}
                {lastOption[overnightMethodCurrent]}
              </Text.Normal>
            )}
            {showDescription && (
              <Box
                background="grey400"
                padding="s24"
                borderRadius={{
                  all: 'abs008',
                }}
                gap="s12"
              >
                <Text.Normal size="200" color="grey800" weight="medium">
                  {methodsDescription[overnightMethod]}
                </Text.Normal>
              </Box>
            )}
            {showButton && (
              <Box alignItems="flexEnd">
                <Button onClick={onClickSelector}>{i18n.t('common.apply')}</Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Section>
  )
}

export default observer(OvernightShifts)
